import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import subscriptionApiService, { SubscriptionInfo } from '../api/subscriptionApi';

interface TenantContextData {
  tenant: SubscriptionInfo | null;
  isLoading: boolean;
  error: string | null;
  hasFeature: (feature: string) => boolean;
  isTrialActive: () => boolean;
  trialDaysRemaining: () => number;
  refreshTenant: () => Promise<void>;
}

interface TenantProviderProps {
  children: ReactNode;
}

const TenantContext = createContext<TenantContextData>({} as TenantContextData);

export function TenantProvider({ children }: TenantProviderProps) {
  const { isAuthenticated } = useAuth();
  const [tenant, setTenant] = useState<SubscriptionInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadTenant = useCallback(async () => {
    if (!isAuthenticated) {
      setTenant(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const data = await subscriptionApiService.getCurrentSubscription();
      setTenant(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao carregar dados da empresa');
      setTenant(null);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    loadTenant();
  }, [loadTenant]);

  const hasFeature = useCallback((feature: string): boolean => {
    if (!tenant?.assinatura?.plano?.features) return false;
    return tenant.assinatura.plano.features.includes(feature);
  }, [tenant]);

  const isTrialActive = useCallback((): boolean => {
    if (!tenant?.assinatura) return false;
    return tenant.assinatura.status === 'TRIAL';
  }, [tenant]);

  const trialDaysRemaining = useCallback((): number => {
    if (!tenant?.assinatura?.dataFimTrial) return 0;
    const endDate = new Date(tenant.assinatura.dataFimTrial);
    const now = new Date();
    const diffTime = endDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  }, [tenant]);

  const refreshTenant = useCallback(async () => {
    await loadTenant();
  }, [loadTenant]);

  return (
    <TenantContext.Provider
      value={{
        tenant,
        isLoading,
        error,
        hasFeature,
        isTrialActive,
        trialDaysRemaining,
        refreshTenant,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant(): TenantContextData {
  const context = useContext(TenantContext);

  if (!context) {
    throw new Error('useTenant deve ser usado dentro de um TenantProvider');
  }

  return context;
}

export default TenantContext;
